import { BusinessObject } from './business-object.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { BoReference } from './bo-reference.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { BoVisitor } from './bo-visitor.js'
import { Expression } from '../data/text-or-code.js'
import type { ServiceIntegrationAvailabilityOnClientType } from '../types.js'

export const ServiceIntegrationTypes = {
	mail: 'E-Mail',
	openApi: 'OpenAPI',
	soap: 'SOAP',
	graphql: 'GraphQL',
	sap: 'SAP',
} as const
export type ServiceIntegrationType = keyof typeof ServiceIntegrationTypes

export const ServiceIntegrationContentTypes = {
	json: { name: 'JSON', mimeType: 'application/json' },
	yaml: { name: 'YAML', mimeType: 'application/x-yaml' },
} as const
export type ServiceIntegrationContentType =
	keyof typeof ServiceIntegrationContentTypes
export const ServiceIntegrationAuthenticationTypes = {
	none: 'None (anonymous)',
	bearerToken: 'Bearer token',
	basic: 'Basic (username and password)',
} as const
export type ServiceIntegrationAuthenticationType =
	keyof typeof ServiceIntegrationAuthenticationTypes
export const ServiceIntegrationAvailabilityOnClientTypes: Record<
	ServiceIntegrationAvailabilityOnClientType,
	string
> = {
	none: 'None (only usable on server-side)',
	builtin:
		'Built into client app (use only when intending to call ServiceIntegration directly from client-side)',
}

@jsonClass('ServiceIntegration')
export class ServiceIntegration extends BusinessObject {
	@jsonProperty(String)
	interfaceType: ServiceIntegrationType = 'mail'
	@jsonProperty(BoReference)
	credentialRef?: BoReference<'ConfigurationItem'>

	@jsonProperty(String)
	schemaUrl?: string
	@jsonProperty(String)
	schemaContent: string = ''
	@jsonProperty(String)
	schemaContentType: ServiceIntegrationContentType = 'json'
	@jsonProperty(Expression)
	baseUrlExpression = new Expression()
	@jsonProperty(String)
	authenticationType: ServiceIntegrationAuthenticationType = 'none'
	@jsonProperty(Expression)
	authenticationExpression = new Expression()

	@jsonProperty(Boolean)
	needsCsrfToken = false
	@jsonProperty(String)
	availabilityOnClient: ServiceIntegrationAvailabilityOnClientType = 'none'

	constructor(init?: Partial<ServiceIntegration>) {
		super()
		assignCommonProperties(this, init)
	}

	visit(visitor: BoVisitor): void {
		if (this.credentialRef)
			visitor.visitBoReference(this.credentialRef, ['credentialRef'])
	}

	getServiceUrlOptions(): string[] {
		if (!this.schemaContent?.trim()) return []

		const schemaObj = JSON.parse(this.schemaContent)
		const urls = schemaObj?.servers?.map((server: any) => server.url) ?? []
		return urls
	}

	getClassNames() {
		return [this.getQualifiedName()]
	}
}
